import React from 'react';
import ReactHtmlParser from 'html-react-parser';
import { OrangeCircleTick } from '../../assets/svg';

interface Offer {
  offer_badge_string: string;
  offer_substring: string;
  offer_ends_string: string;
}

interface Plan {
  after_billing_end_string: string;
  after_billing_end_string_without_hours: string;
  billing_period: string;
  checkout_page_offer_string: string;
  crossed_price: string;
  group_classes_access: boolean;
  hourly_price_string: string;
  is_best_value: boolean;
  is_free_month_offer: number;
  is_lto: number;
  offer: Offer;
  plan_name: string;
  plan_type: string;
  prepay_discount: string;
  price: string;
  price_monthly: string;
  price_without_currency: string;
  recordings_access: boolean;
  save_string: string;
  session_cost: string;
  currency_symbol: string;
  sessions_per_four_weeks: number;
  sessions_per_week: number;
  personalised_pricing_data: {
    crossed_price: string;
    price: string;
    save_string: string;
  };
}

interface PlanTableDesktopProps {
  planData: Plan[];
}

const PlanTableDesktop: React.FC<PlanTableDesktopProps> = ({ planData }) => {
  if (!planData || planData.length === 0) return null;

  const headers = ['', ...planData.map((plan) => plan.plan_name)];

  const rows = [
    {
      label: 'Number of 1-on-1 Sessions per week',
      values: planData.map((p) => (p.sessions_per_week ? p.sessions_per_week : 'N/A'))
    },
    {
      label: 'Number of 1-on-1 Sessions per 4 weeks',
      values: planData.map((p) => (p.sessions_per_four_weeks ? p.sessions_per_four_weeks : 'N/A'))
    },
    {
      label: '1-on-1 Session Cost',
      values: planData.map((p, i) => {
        const price = p.hourly_price_string.replace('/hourly', '');
        return parseFloat(price.replace(/[^0-9.]/g, '')) > 0 ? (
          <div key={i}>
            {p.currency_symbol}
            {price}
            <span className="text-[#9B9B9B] text-[11.5px] font-[550]">/hr</span>
          </div>
        ) : (
          'N/A'
        );
      })
    },
    {
      label: 'Access to 50+ daily live group classes',
      values: planData.map((p) =>
        p.group_classes_access ? <OrangeCircleTick className={'w-[24px]'} /> : '✖'
      )
    },
    {
      label: 'Access to recordings',
      values: planData.map((p) =>
        p.recordings_access ? <OrangeCircleTick className={'w-[24px]'} /> : '✖'
      )
    },
    {
      label: 'Billing period',
      values: planData.map((p) => p.billing_period)
    },
    {
      label: 'Monthly base price',
      values: planData.map((p, i) => {
        return (
          <div key={i}>
            {p.price_monthly}
            <span className="text-[#9B9B9B] text-[11.5px] font-[550]">/mo</span>
          </div>
        );
      })
    }
  ];

  const renderOfferCard = (plan: Plan) => {
    const price = plan.personalised_pricing_data.price;
    const crossedPrice = plan.personalised_pricing_data.crossed_price;
    const saveString = plan.personalised_pricing_data.save_string;
    return (
      <>
        <p className="text-[24px] font-bold leading-[normal] text-[#000] flex items-center mb-[8px]">
          {price}
          {crossedPrice && (
            <span className="text-[15px] font-bold leading-[normal] text-[#BFBFBF] line-through ml-[6px]">
              {crossedPrice}
            </span>
          )}
        </p>

        {saveString && (
          <p className="text-[11.5px] font-semibold leading-[16px] text-[#FF006A] mb-[5px] text-left">
            <span className="text-[#636363]">{ReactHtmlParser(saveString)}</span>
          </p>
        )}

        <p className="text-[11.5px] font-medium leading-[16px] text-[#969696] price-card text-left">
          {ReactHtmlParser(plan.after_billing_end_string_without_hours)}
        </p>

        {/* <button
          onClick={() => onSelectPlan && onSelectPlan(plan)}
          className="w-full mt-[15px] mb-[14px] bg-[#ee731b] hover:bg-[#CC4B11] text-[15px] font-semibold leading-[22px] text-white pt-[12.5px] pb-[11.5px] rounded-[14px] px-[18px]"
        >
          Get this plan
        </button>
        <div className="flex items-center justify-between w-full gap-[10px] flex-wrap">
          {plan.offer.offer_ends_string && (
            <p className="text-[11.5px] font-[550] leading-[18px] text-[#FF0F73]">
              {plan.offer.offer_ends_string}
            </p>
          )}

          {plan.is_best_value && (
            <p className="text-[10px] font-semibold leading-[normal] text-white bg-[#FF006A] px-[6px] py-[3px] rounded-[6px] w-fit">
              Best Value
            </p>
          )}
        </div> */}
      </>
    );
  };

  return (
    <>
      <table
        id="plan-table-desktop"
        className="font-monaSans border border-[#EBEBEB] rounded-[18px] !border-separate ![border-spacing:0]"
      >
        <thead>
          <tr>
            {headers.map((text, i) => {
              const isBestValue = i > 0 && planData[i - 1].is_best_value;
              return (
                <th
                  key={i}
                  className={`max-w-[240px] text-[14px] font-semibold leading-[normal] text-[#ee731b] px-[40px] py-[20px]
                  ![border-top:0px] ![border-bottom:1px_solid_#EBEBEB]
                  ${i === 0 ? 'text-left rounded-tl-[14px] ![border-left:0px]' : 'text-center'}
                  ${i === headers.length - 1 ? 'rounded-tr-[14px] ![border-right:0px]' : '![border-right:1px_solid_#EBEBEB]'}
                  ${isBestValue ? 'bg-[#FFD4B51A]' : 'text-[#ee731b]'}
                `}
                >
                  {text}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              <td className="px-[25px] py-[22px] text-left text-[15px] font-[550] leading-[normal] text-[#555555] w-[350px] ![border-top:0px] ![border-left:0px] ![border-right:1px_solid_#EBEBEB] ![border-bottom:1px_solid_#EBEBEB]">
                {row.label}
              </td>
              {row.values.map((val, j) => {
                const plan = planData[j];
                return (
                  <td
                    key={j}
                    className={`text-center text-[14.5px] font-[550] leading-[normal] ![border-top:0px] ![border-bottom:1px_solid_#EBEBEB]
                      ${j === planData.length - 1 ? '![border-right:0px]' : '![border-right:1px_solid_#EBEBEB]'}
                      ${plan.is_best_value ? 'bg-[#FFD4B51A]' : ''}
                    `}
                  >
                    <div className="flex justify-center">{val}</div>
                  </td>
                );
              })}
            </tr>
          ))}

          <tr className="bg-[#FAFAFA80] rounded-bl-[18px]">
            <td
              className="text-left ![border-top:1px_solid_#EBEBEB] ![border-bottom:0px] ![border-left:0px] ![border-right:1px_solid_#EBEBEB]"
              style={{ display: 'flex' }}
            >
              <div className="px-[25px] py-[22px] rounded-bl-[14px]">
                <p className="text-[15px] font-[550] leading-[normal] text-[#555555]">
                  Available offers
                </p>
              </div>
            </td>
            {planData.map((plan, i) => (
              <td
                key={i}
                className={`align-top p-0 ![border-top:1px_solid_#EBEBEB] ![border-bottom:0px]
                  ${i === planData.length - 1 ? 'rounded-br-[14px] ![border-right:0px]' : '![border-right:1px_solid_#EBEBEB]'}
                  ${plan.is_best_value ? 'bg-[#fffaf7]' : 'bg-[#FAFAFA80]'}
                `}
              >
                <div
                  key={i}
                  className={`p-[25px] ${
                    plan.is_best_value ? 'bg-[#fffaf7]' : 'bg-[#FAFAFA80] '
                  } ${i === planData.length - 1 ? 'rounded-br-[14px]' : ''}`}
                >
                  {renderOfferCard(plan)}
                </div>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </>
  );
};

export default PlanTableDesktop;
